import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalyticsFilters = ({ onFiltersChange }) => {
  const [dateRange, setDateRange] = useState('1M');
  const [selectedAccounts, setSelectedAccounts] = useState(['main']);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: ''
  });
  const [showCustomDate, setShowCustomDate] = useState(false);

  // Mock data for filters
  const dateRangeOptions = [
    { value: '1D', label: '1 Day', description: 'Last 24 hours' },
    { value: '1W', label: '1 Week', description: 'Last 7 days' },
    { value: '1M', label: '1 Month', description: 'Last 30 days' },
    { value: '3M', label: '3 Months', description: 'Last 90 days' },
    { value: 'YTD', label: 'YTD', description: 'Year to date' },
    { value: 'custom', label: 'Custom', description: 'Select date range' }
  ];

  const accounts = [
    { id: 'main', name: 'Main Trading Account', balance: 125847.32, type: 'Live' },
    { id: 'demo', name: 'Demo Account', balance: 50000.00, type: 'Demo' },
    { id: 'retirement', name: 'Retirement Portfolio', balance: 245600.88, type: 'Live' },
    { id: 'scalping', name: 'Scalping Account', balance: 75000.00, type: 'Live' }
  ];

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' }
  ];

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value === 'custom') {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
      notifyFiltersChange({ dateRange: value, selectedAccounts, baseCurrency });
    }
  };

  const handleAccountToggle = (accountId) => {
    const newSelectedAccounts = selectedAccounts?.includes(accountId)
      ? selectedAccounts?.filter(id => id !== accountId)
      : [...selectedAccounts, accountId];
    
    setSelectedAccounts(newSelectedAccounts);
    notifyFiltersChange({ dateRange, selectedAccounts: newSelectedAccounts, baseCurrency });
  };

  const handleCurrencyChange = (currency) => {
    setBaseCurrency(currency);
    setShowCurrencyDropdown(false);
    notifyFiltersChange({ dateRange, selectedAccounts, baseCurrency: currency });
  };

  const handleCustomDateSubmit = () => {
    if (customDateRange?.start && customDateRange?.end) {
      setShowCustomDate(false);
      notifyFiltersChange({ 
        dateRange: `${customDateRange?.start} to ${customDateRange?.end}`, 
        selectedAccounts, 
        baseCurrency 
      });
    }
  };

  const notifyFiltersChange = (filters) => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const getSelectedAccountsText = () => {
    if (selectedAccounts?.length === 0) return 'No accounts selected';
    if (selectedAccounts?.length === 1) {
      const account = accounts?.find(acc => acc?.id === selectedAccounts?.[0]);
      return account ? account?.name : 'Unknown account';
    }
    return `${selectedAccounts?.length} accounts selected`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        {/* Date Range Picker */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Date Range
            </label>
            <div className="flex flex-wrap gap-2">
              {dateRangeOptions?.map((option) => (
                <Button
                  key={option?.value}
                  variant={dateRange === option?.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleDateRangeChange(option?.value)}
                  className="text-xs"
                >
                  {option?.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Custom Date Range */}
          {showCustomDate && (
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customDateRange?.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e?.target?.value }))}
                  className="px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={customDateRange?.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e?.target?.value }))}
                  className="px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={handleCustomDateSubmit}
                iconName="Check"
                className="h-10"
              />
            </div>
          )}
        </div>

        {/* Account and Currency Selectors */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Account Multi-Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-foreground mb-2">
              Accounts
            </label>
            <Button
              variant="outline"
              onClick={() => setShowAccountDropdown(!showAccountDropdown)}
              className="w-full sm:w-64 justify-between"
            >
              <span className="truncate">{getSelectedAccountsText()}</span>
              <Icon name="ChevronDown" size={16} />
            </Button>

            {showAccountDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 animate-fade-in">
                <div className="p-2 max-h-64 overflow-y-auto">
                  {accounts?.map((account) => (
                    <label
                      key={account?.id}
                      className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedAccounts?.includes(account?.id)}
                        onChange={() => handleAccountToggle(account?.id)}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {account?.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatCurrency(account?.balance, baseCurrency)} • {account?.type}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Base Currency Selector */}
          <div className="relative">
            <label className="block text-sm font-medium text-foreground mb-2">
              Base Currency
            </label>
            <Button
              variant="outline"
              onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
              className="w-full sm:w-32 justify-between"
            >
              <span>{baseCurrency}</span>
              <Icon name="ChevronDown" size={16} />
            </Button>

            {showCurrencyDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 animate-fade-in">
                <div className="p-1">
                  {currencies?.map((currency) => (
                    <button
                      key={currency?.code}
                      onClick={() => handleCurrencyChange(currency?.code)}
                      className={`w-full text-left p-2 rounded-md hover:bg-muted transition-colors duration-200 ${
                        baseCurrency === currency?.code ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-foreground">
                        {currency?.code}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {currency?.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RotateCcw"
            onClick={() => {
              setDateRange('1M');
              setSelectedAccounts(['main']);
              setBaseCurrency('USD');
              setShowCustomDate(false);
              notifyFiltersChange({ dateRange: '1M', selectedAccounts: ['main'], baseCurrency: 'USD' });
            }}
          >
            Reset
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Bookmark"
            title="Save current filter combination"
          >
            Save
          </Button>
        </div>
      </div>
      {/* Click outside handlers */}
      {(showAccountDropdown || showCurrencyDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowAccountDropdown(false);
            setShowCurrencyDropdown(false);
          }}
        />
      )}
    </div>
  );
};

export default AnalyticsFilters;